import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import FilterTestResults from "./FilterTestResults";
import { FilterTestRow, PromptDesign } from "./types";

const EMPTY_TEST_ROWS: FilterTestRow[] = [];

interface PayloadPlaygroundProps {
  design: PromptDesign | undefined;
  promptPayload: string;
  onPromptPayloadChange: (text: string) => void;
  samplePostsText: string;
  onSamplePostsTextChange: (text: string) => void;
  onTest: () => void;
  isTesting: boolean;
  testRows: FilterTestRow[] | undefined;
  onSave: () => void;
  isSaving: boolean;
  saveSuccess: boolean;
  canTest: boolean;
  canSave: boolean;
}

function ReviewNotes({ design }: { design: PromptDesign | undefined }) {
  const ambiguities = design?.ambiguities ?? [];
  const warnings = design?.warnings ?? [];
  if (ambiguities.length === 0 && warnings.length === 0) return null;
  const items = [...warnings, ...ambiguities];
  return (
    <div
      className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4"
      data-testid="review-notes"
    >
      <p className="text-sm font-semibold text-amber-800">
        Review before saving
      </p>
      <p className="mt-1 text-xs text-amber-700">
        The source was ambiguous, sparse, or contradictory in places. Check the
        prompt below and edit as needed.
      </p>
      <ul className="mt-2 list-disc pl-5 text-sm text-amber-800">
        {items.map((item, index) => (
          <li key={`review-${index}`}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

const PayloadPlayground = ({
  design,
  promptPayload,
  onPromptPayloadChange,
  samplePostsText,
  onSamplePostsTextChange,
  onTest,
  isTesting,
  testRows,
  onSave,
  isSaving,
  saveSuccess,
  canTest,
  canSave,
}: PayloadPlaygroundProps) => {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-gray-800">
        2. Payload Preview &amp; Playground
      </h2>
      <p className="mt-1 text-sm text-gray-500">
        Review and edit the generated customer prompt, run a test against sample
        posts, then save the active definition.
      </p>

      <ReviewNotes design={design} />

      <label className="mt-4 block">
        <span className="text-sm font-medium text-gray-700">
          Customer prompt payload (editable)
        </span>
        <textarea
          className="mt-1 h-56 w-full rounded-md border border-gray-300 p-2 font-mono text-sm focus:border-green-500 focus:outline-none"
          placeholder="Generate a prompt or paste customer behavior rules here…"
          value={promptPayload}
          onChange={(e) => onPromptPayloadChange(e.target.value)}
          data-testid="prompt-payload"
        />
        <span className="mt-1 block text-xs text-gray-400">
          Write categories for what should stay on the report. Everything else
          is removed. Use Not in scope examples to sharpen the boundary — for
          example a friend&apos;s past use, a policy debate with no personal
          use, or a hypothetical such as &quot;if I used…&quot; with no
          admission. If Definition and Not in scope disagree, edit or regenerate
          before saving.
        </span>
      </label>

      <div className="mt-6 border-t border-gray-100 pt-5">
        <span className="text-sm font-medium text-gray-700">
          Sample posts (one per line)
        </span>
        <p className="mt-1 text-xs text-gray-500">
          Paste sample misconduct posts, then run Test filter to see how the AI
          classifies each one with this prompt.
        </p>
        <textarea
          className="mt-1 h-32 w-full rounded-md border border-gray-300 p-2 text-sm focus:border-green-500 focus:outline-none"
          placeholder={
            "Just got paid to post this #ad\nLoving the new product launch!"
          }
          value={samplePostsText}
          onChange={(e) => onSamplePostsTextChange(e.target.value)}
          data-testid="sample-posts"
        />
        <div className="mt-3">
          <Button
            text={isTesting ? "Testing…" : "Test filter"}
            type={isTesting ? ButtonTypes.saving : ButtonTypes.default}
            colorType={ButtonColorTypes.outline}
            disabled={!canTest || isTesting}
            onClick={onTest}
            data-testid="test-button"
          />
        </div>

        <FilterTestResults
          rows={testRows ?? EMPTY_TEST_ROWS}
          filteredReportTestId="manual-filtered-report"
        />
      </div>

      <div className="mt-6 flex items-center gap-3 border-t border-gray-100 pt-5">
        <Button
          text={isSaving ? "Saving…" : "Save active prompt"}
          type={isSaving ? ButtonTypes.saving : ButtonTypes.default}
          colorType={ButtonColorTypes.primary}
          disabled={!canSave || isSaving}
          onClick={onSave}
          data-testid="save-button"
        />
        {saveSuccess && <span className="text-sm text-green-600">Saved.</span>}
      </div>
    </section>
  );
};

export default PayloadPlayground;
